import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { GrowthNav } from './GrowthNav';
import { useGrowthAudits } from '../../hooks/useGrowthAudits';
import { useGrowthTasks } from '../../hooks/useGrowthTasks';
import { useGrowthJobs } from '../../hooks/useGrowthJobs';

const Page = styled.div`
  background: var(--background);
  padding: 0;
  min-height: 100%;
`;

const Card = styled.div`
  padding: 1.25rem;
  border-radius: 14px;
  background: var(--surface);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--outline);
`;

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ScoreCard = styled(Card)<{ $variant?: 'green' | 'orange' | 'blue' }>`
  text-align: center;
  .score {
    font-size: 2rem;
    font-weight: 700;
    color: ${({ $variant }) =>
      $variant === 'green' ? 'var(--success)' : $variant === 'orange' ? 'var(--warning)' : 'var(--primary)'};
  }
  .label {
    font-size: 0.85rem;
    color: var(--onSurfaceVariant);
    text-transform: capitalize;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const AlertList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const AlertItem = styled.li`
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--outline);
  font-size: 0.9rem;
  &:last-child {
    border-bottom: none;
  }
`;

const ActionLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

const Btn = styled.button<{ $primary?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid var(--outline);
  background: ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $primary }) => ($primary ? 'var(--onPrimary)' : 'var(--onSurface)')};
  cursor: pointer;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function GrowthOverviewPage() {
  const { data: audits = [], isLoading: auditsLoading } = useGrowthAudits();
  const { data: tasks = [] } = useGrowthTasks();
  const openTasks = tasks.filter((t) => t.status === 'todo' || t.status === 'doing');
  const topActions = openTasks.slice(0, 7);
  const { runSnapshot, runAudits, runBrief, snapshotLoading, auditsLoading: auditsJobLoading, briefLoading } = useGrowthJobs();

  const latestByType = (type: string) =>
    audits.find((a) => a.audit_type === type);

  const visitor = latestByType('visitor_readiness');
  const local = latestByType('local_discovery');
  const momentum = latestByType('engagement_momentum');

  const alerts: { title: string; severity: string }[] = [];
  for (const a of [visitor, local, momentum].filter(Boolean)) {
    if (a && a.score < 50)
      alerts.push({
        title: `${a.audit_type.replace(/_/g, ' ')} score low: ${a.score}`,
        severity: 'high',
      });
    a?.findings?.slice(0, 2).forEach((f) => {
      if (f.severity === 'high' || f.severity === 'medium')
        alerts.push({ title: f.title, severity: f.severity });
    });
  }

  return (
    <Page>
      <GrowthNav />
      <SectionTitle style={{ marginTop: 0 }}>Local Engagement</SectionTitle>
      <div style={{ marginBottom: '1rem' }}>
        <Btn $primary onClick={() => runSnapshot()} disabled={snapshotLoading}>
          {snapshotLoading ? 'Running…' : 'Run snapshot now'}
        </Btn>
        <Btn onClick={() => runAudits()} disabled={auditsJobLoading}>
          {auditsJobLoading ? 'Running…' : 'Run audits'}
        </Btn>
        <Btn onClick={() => runBrief()} disabled={briefLoading}>
          {briefLoading ? 'Running…' : 'Generate brief'}
        </Btn>
      </div>

      <ScoreGrid>
        <ScoreCard $variant="blue">
          <div className="score">{visitor?.score ?? '—'}</div>
          <div className="label">Visitor Readiness</div>
        </ScoreCard>
        <ScoreCard $variant="green">
          <div className="score">{local?.score ?? '—'}</div>
          <div className="label">Local Discovery</div>
        </ScoreCard>
        <ScoreCard $variant="orange">
          <div className="score">{momentum?.score ?? '—'}</div>
          <div className="label">Engagement Momentum</div>
        </ScoreCard>
      </ScoreGrid>

      {auditsLoading && <p>Loading audits…</p>}

      <Card style={{ marginBottom: '1.5rem' }}>
        <SectionTitle>Alerts</SectionTitle>
        {alerts.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--onSurfaceVariant)' }}>No high-priority alerts.</p>
        ) : (
          <AlertList>
            {alerts.slice(0, 10).map((al, i) => (
              <AlertItem key={i}>
                <span style={{ color: al.severity === 'high' ? 'var(--error)' : 'var(--warning)' }}>
                  [{al.severity}]
                </span>{' '}
                {al.title}
              </AlertItem>
            ))}
          </AlertList>
        )}
      </Card>

      <Card>
        <SectionTitle>Top 7 actions</SectionTitle>
        {topActions.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--onSurfaceVariant)' }}>No open tasks. <ActionLink to="/growth/tasks">View all tasks</ActionLink>.</p>
        ) : (
          <AlertList>
            {topActions.map((t) => (
              <AlertItem key={t.id}>
                <ActionLink to="/growth/tasks">{t.title}</ActionLink>
                <span style={{ color: 'var(--onSurfaceVariant)', marginLeft: '0.5rem' }}>({t.impact})</span>
              </AlertItem>
            ))}
          </AlertList>
        )}
      </Card>
    </Page>
  );
}
